import { computed, shallowRef, watch } from 'vue'
import { useEventListener, useWindowSize } from '@vueuse/core'
import { BREAKPOINTS } from '@/constants/breakpoints'

export function useSidePanelResize(props, emit) {
  const { width: viewportWidth } = useWindowSize()
  const drag = shallowRef(null)
  const resizing = computed(() => drag.value !== null)
  // A mouse on the handle, and how far down it the pointer is, so a tooltip can sit beside it.
  const hovered = shallowRef(false)
  const hoverY = shallowRef(0)

  const maximum = computed(() => Math.min(props.maxWidth, viewportWidth.value))
  const minimum = computed(() => Math.min(props.minWidth, maximum.value))
  const clamp = (width) => Math.round(Math.min(maximum.value, Math.max(minimum.value, width)))
  const width = computed(() => props.resizable ? clamp(props.width) : props.width)
  // Below `md` the panel is full width, so there is no edge to drag.
  const enabled = computed(() => props.open && props.resizable && viewportWidth.value >= BREAKPOINTS.md)

  useEventListener('pointermove', (event) => {
    if (!drag.value || event.pointerId !== drag.value.pointerId) return
    const direction = props.side === 'right' ? -1 : 1
    emit('update:width', clamp(drag.value.width + (event.clientX - drag.value.x) * direction))
  })

  useEventListener(['pointerup', 'pointercancel'], (event) => {
    if (event.pointerId === drag.value?.pointerId) drag.value = null
  })
  useEventListener('blur', () => { drag.value = null })

  const start = (event) => {
    if (!enabled.value || event.button !== 0 || !event.isPrimary) return
    event.preventDefault()
    event.currentTarget.focus({ preventScroll: true })
    drag.value = { x: event.clientX, width: width.value, pointerId: event.pointerId }
  }

  // A finger has no hover, and its drag works without one.
  const hover = (event) => {
    if (event.pointerType === 'touch') return
    hoverY.value = Math.round(event.clientY - event.currentTarget.getBoundingClientRect().top)
    hovered.value = true
  }

  const leave = () => { hovered.value = false }

  const keydown = (event) => {
    if (!enabled.value) return
    const step = event.shiftKey ? 50 : 10
    const direction = props.side === 'right' ? -1 : 1
    const widths = {
      ArrowLeft: width.value - step * direction,
      ArrowRight: width.value + step * direction,
      Home: minimum.value,
      End: maximum.value,
    }
    if (!(event.key in widths)) return
    event.preventDefault()
    event.stopPropagation()
    emit('update:width', clamp(widths[event.key]))
  }

  watch(enabled, (value) => {
    if (value) return
    drag.value = null
    hovered.value = false
  }, { flush: 'sync' })

  return { width, minimum, maximum, enabled, resizing, hovered, hoverY, start, hover, leave, keydown }
}
